import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Clock, PencilLine } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatClockTime } from '@/lib/format';
import { applyFieldErrors } from '@/lib/forms';
import type { SchoolTimings } from '@/types/staffAttendance';
import { staffAttendanceApi } from '../api/staffAttendance.api';
import { timingsSchema, type TimingsFormValues } from '../schemas/timings.schema';

export interface SchoolTimingsBarProps {
  /** `INSTITUTE_PROFILE_EDIT` — whether the Change button is offered. The server checks it again. */
  canEdit: boolean;
  /** Raised after a save, so the register can re-judge who was late. */
  onSaved: (timings: SchoolTimings) => void;
}

/**
 * The two times the whole register runs on, shown above it and changed from it.
 *
 * Punch-in after the start time is marked late; at the closing time sign-in
 * closes and anybody still in is signed out automatically. Both are the
 * school's own settings, so a change takes effect at once for everyone.
 */
export function SchoolTimingsBar({ canEdit, onSaved }: SchoolTimingsBarProps) {
  const { data, isLoading, error, refetch } = useApiQuery(() => staffAttendanceApi.timings(), []);
  const [isEditing, setIsEditing] = useState(false);

  return (
    <>
      <div className="card flex flex-col gap-3.5 p-4 sm:p-4.5 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-slate-200/80 dark:border-white/10 bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-white dark:from-slate-900/60 dark:via-indigo-950/20 dark:to-slate-900/40 shadow-xs">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-indigo-500/20">
            <Clock className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-extrabold text-slate-800 dark:text-white">School timings</h2>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-950/80 dark:text-blue-300">
                Official
              </span>
            </div>
            {isLoading ? (
              <p className="text-xs text-slate-400 mt-0.5">Loading timings…</p>
            ) : error || !data ? (
              <p className="text-xs text-slate-500 mt-0.5">
                Could not load the school timings.{' '}
                <button
                  type="button"
                  onClick={refetch}
                  className="font-semibold text-indigo-600 dark:text-indigo-400 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent cursor-pointer"
                >
                  Try again
                </button>
              </p>
            ) : (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600 text-sm dark:text-slate-400">Punch-in:</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg font-mono font-bold text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/80 dark:border-slate-700 shadow-2xs">
                    {formatClockTime(data.startsAt)}
                  </span>
                  <span className="text-sm text-slate-600 dark:text-slate-400">(later is marked late)</span>
                </div>
                <span className="hidden sm:inline text-slate-300 dark:text-slate-600" aria-hidden>·</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-600 text-sm dark:text-slate-400">Sign-out:</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-lg font-mono font-bold text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200/80 dark:border-slate-700 shadow-2xs">
                    {formatClockTime(data.endsAt)}
                  </span>
                  <span className="text-sm text-slate-600 dark:text-slate-400">(auto sign-out)</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {canEdit && data && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<PencilLine className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
            onClick={() => setIsEditing(true)}
            className="self-start sm:self-center shrink-0 rounded-xl font-bold border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 shadow-2xs cursor-pointer"
          >
            Change timings
          </Button>
        )}
      </div>

      {data && (
        <TimingsModal
          isOpen={isEditing}
          current={data}
          onClose={() => setIsEditing(false)}
          onSaved={(saved) => {
            setIsEditing(false);
            refetch();
            onSaved(saved);
          }}
        />
      )}
    </>
  );
}

interface TimingsModalProps {
  isOpen: boolean;
  current: SchoolTimings;
  onClose: () => void;
  onSaved: (timings: SchoolTimings) => void;
}

function TimingsModal({ isOpen, current, onClose, onSaved }: TimingsModalProps) {
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<TimingsFormValues>({
    resolver: zodResolver(timingsSchema),
    defaultValues: current,
  });
  const {
    register,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (!isOpen) return;
    setFormError(null);
    reset(current);
  }, [isOpen, current, reset]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      const saved = await staffAttendanceApi.updateTimings(values);
      toast.success(
        'School timings saved',
        `Punch-in from ${formatClockTime(saved.startsAt)}, closing at ${formatClockTime(saved.endsAt)}.`,
      );
      onSaved(saved);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(form.setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title="Change school timings"
      description="These apply to every member of staff from now on. Days already recorded keep their times."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} isLoading={isSubmitting}>
            Save timings
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save the timings">
            {formError}
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="School starts (punch-in)"
            type="time"
            hint="A punch-in after this is marked late."
            error={errors.startsAt?.message}
            {...register('startsAt')}
          />
          <Input
            label="School ends (punch-out)"
            type="time"
            hint="Sign-in closes, and anyone still in is signed out."
            error={errors.endsAt?.message}
            {...register('endsAt')}
          />
        </div>
      </form>
    </Modal>
  );
}
